import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {styled, Text, Button} from "@reside/ui";

import {GeneralSearch} from "../search-box";
import {DataItem} from "../search-box/Dropdown";
import {SimpleFacilityModel} from "../../utils/types/facility";

type Props = Readonly<{
  facilities: ReadonlyArray<SimpleFacilityModel>;
  selectedFacilityId: string | null;
  onSelect: (facilityId: string | null) => void;
}>;

export const FacilitySelect = ({
  facilities,
  selectedFacilityId,
  onSelect,
}: Props) => {
  const [searchValue, setSearchValue] = useState("");
  const [isChanging, setIsChanging] = useState(selectedFacilityId === null);

  const selectedFacility = useMemo(
    () => facilities.find(facility => facility.id === selectedFacilityId),
    [facilities, selectedFacilityId],
  );

  useEffect(() => {
    if (selectedFacilityId) {
      setIsChanging(false);
    }
  }, [selectedFacilityId]);

  const filteredFacilities = useMemo(
    () =>
      facilities.filter(facility =>
        facility.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [facilities, searchValue],
  );

  const searchItems = useMemo(
    () => [
      {
        label: "Facility",
        data: filteredFacilities.map(facility => ({
          id: facility.id,
          value: facility.name,
        })),
      },
    ],
    [filteredFacilities],
  );

  const handleSelect = useCallback(
    (item: DataItem | null) => {
      if (!item?.id) {
        return;
      }

      onSelect(item.id);
      setSearchValue("");
      setIsChanging(false);
    },
    [onSelect],
  );

  const handleChangeClick = useCallback(() => {
    onSelect(null);
    setSearchValue("");
    setIsChanging(true);
  }, [onSelect]);

  if (selectedFacility && !isChanging) {
    return (
      <FieldWrapper>
        <SelectedBar data-test-id="startWorkflowFacilitySelect">
          <SelectedName>{selectedFacility.name}</SelectedName>
        </SelectedBar>
        <Button
          data-test-id="startWorkflowFacilityChange"
          onClick={handleChangeClick}
          outline
          flat
          ghost
          underline
          color="primary"
        >
          Change
        </Button>
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper>
      <GeneralSearch
        autoFocus={isChanging}
        hideSuggestion={false}
        inputValue={searchValue}
        isSearching={false}
        itemToString={item => item?.value ?? ""}
        items={searchItems}
        onChange={handleSelect}
        onClearClick={() => setSearchValue("")}
        onInputChange={(event: FormEvent<HTMLInputElement>) =>
          setSearchValue(event.currentTarget.value)
        }
        onPressSpace={() => {
          if (searchValue.trim().length > 0) {
            setSearchValue(`${searchValue} `);
          }
        }}
        placeholder="Search facilities"
        showClearButton={searchValue.length > 0}
        testId="startWorkflowFacilitySearch"
      />
    </FieldWrapper>
  );
};

const FieldWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  height: 37px;
  gap: ${({theme}) => theme.space[4]}px;
`;

const SelectedBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.space[2]}px;
  width: 100%;
  min-height: 32px;
  padding: 3px 4px 3px 16px;
  border-radius: 8px;
  background: ${({theme}) => theme.color.white};
  box-sizing: border-box;
  height: 100%;
`;

const SelectedName = styled(Text)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.25px;
  color: ${({theme}) => theme.color.darkBlue100};
`;
